import React, { useRef, useState } from 'react'
import {
  ClickAwayListener,
  Grow,
  IconButton,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core'
import {
  mdiDelete,
  mdiDotsVertical,
  mdiEyeOutline,
  mdiNoteEditOutline,
  mdiSendOutline,
} from '@mdi/js'
import Icon from '@mdi/react'
import { useDispatch } from 'react-redux'

import colors from '../../../assets/colors'
import fontSizes from '../../../assets/fontSizes'
import { dispatchAlert, remove } from '../AlertsActions'

import ConfirmModal from './ConfirmModal'
import RecipientsModal from './RecipientsModal'

const useStyles = makeStyles({
  paper: {
    marginRight: 0,
    borderRadius: '10px',
    color: colors.textPrimary,
    fontSize: fontSizes.text,
    boxShadow: '0px 0px 7px 0px #00000040',
  },
  ref: {
    width: '34px',
    height: '34px',
    position: 'absolute',
    backgroundColor: '#FFFF',
    transform: 'rotate(45deg)',
    right: '7px',
    '&:hover': {
      backgroundColor: '#FFFF',
    },
  },
  menuList: {
    padding: '10px',
  },
  menuItem: {
    padding: '2px',
    height: 'auto',
    lineHeight: '1',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
      color: colors.primary,
    },
  },
  menuItemLi: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    padding: 0,
    margin: 0,
    borderRadius: 0,
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
      color: colors.primary,
    },
    fontSize: '14px',
  },
  menuItemLiIcon: {
    marginRight: '8px',
  },
})

// Menu ⋮ de ações do alerta (Leituras / Editar / Disparar / Excluir)
const ActionsMenu = ({ alert, onEdit, onDone = () => {}, iconColor }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const [showRecipients, setShowRecipients] = useState(false)
  // 'dispatch' | 'remove': mantido após fechar para não esvaziar o popup na animação
  const [confirmAction, setConfirmAction] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const anchorRef = useRef(null)

  const handleClose = event => {
    if (anchorRef.current?.contains(event?.target)) return
    setOpen(false)
  }

  const handleListKeyDown = e => {
    if (e.key === 'Tab' || e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  const handleEdit = () => {
    setOpen(false)
    onEdit(alert)
  }

  const handleDispatch = () => {
    setOpen(false)
    setConfirmAction('dispatch')
    setConfirmOpen(true)
  }

  const handleRemove = () => {
    setOpen(false)
    setConfirmAction('remove')
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    setConfirmOpen(false)

    if (confirmAction === 'dispatch') await dispatch(dispatchAlert(alert.id))
    if (confirmAction === 'remove') await dispatch(remove(alert.id))

    onDone()
  }

  const popUp = {
    dispatch: {
      title: alert.dispatched ? 'Disparar novamente' : 'Disparar alerta',
      message: alert.dispatched
        ? 'Só as lavanderias que ainda não receberam o alerta serão alcançadas.'
        : 'O alerta será enviado para todas as lavanderias não inativas.',
      confirmText: 'Disparar',
    },
    remove: {
      title: 'Excluir alerta',
      message: 'Após ser apagado o conteúdo não poderá ser recuperado',
      confirmText: 'Excluir',
    },
  }[confirmAction] || { title: '', message: '' }

  const handleRecipients = () => {
    setOpen(false)
    setShowRecipients(true)
  }

  const actions = [
    ...(alert.dispatched
      ? [
          {
            label: 'Ver leituras',
            icon: mdiEyeOutline,
            onClick: handleRecipients,
          },
        ]
      : []),
    { label: 'Editar', icon: mdiNoteEditOutline, onClick: handleEdit },
    {
      label: alert.dispatched ? 'Disparar novamente' : 'Disparar',
      icon: mdiSendOutline,
      onClick: handleDispatch,
    },
    { label: 'Excluir', icon: mdiDelete, onClick: handleRemove },
  ]

  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-controls={open ? `alert-menu-${alert.id}` : undefined}
        aria-haspopup="true"
        size="small"
        onClick={() => setOpen(current => !current)}
      >
        <Icon path={mdiDotsVertical} size={0.9} color={iconColor} />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end"
        style={{ zIndex: 99999999 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top-end' }}>
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id={`alert-menu-${alert.id}`}
                  onKeyDown={handleListKeyDown}
                  className={classes.menuList}
                >
                  <MenuItem
                    className={classes.ref}
                    style={
                      placement === 'bottom-end'
                        ? { top: '-2px' }
                        : { bottom: '-2px' }
                    }
                  />

                  {actions.map(action => (
                    <MenuItem key={action.label} className={classes.menuItem}>
                      <IconButton
                        onClick={action.onClick}
                        className={classes.menuItemLi}
                      >
                        <Icon
                          path={action.icon}
                          size={0.8}
                          className={classes.menuItemLiIcon}
                        />
                        {action.label}
                      </IconButton>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <ConfirmModal
        open={confirmOpen}
        title={popUp.title}
        message={popUp.message}
        confirmText={popUp.confirmText}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      {showRecipients && (
        <RecipientsModal
          alert={alert}
          onClose={() => setShowRecipients(false)}
        />
      )}
    </>
  )
}

export default ActionsMenu
